import { useEffect, useState } from "react"
import { Link, Outlet } from "react-router-dom"
import { BarChart2, DollarSign, FileText, Settings, Truck } from "lucide-react"
import { cn } from "../../lib/utils"
import logo from "../../assets/logo-white.png"
import { Button } from "../ui/button"
import Logout from "../Icons/Logout"
import useAuth from "../../hooks/useAuth"
import Header from "./Header"
import { Layout, Drawer, Grid } from "antd"
import { X } from "lucide-react"
import "antd/dist/reset.css"

const { Sider, Content } = Layout;
const { useBreakpoint } = Grid;

export default function DashboardLayout() {
  const { logOut, user } = useAuth();
  const [activeNav, setActiveNav] = useState("dashboard");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const screens = useBreakpoint();
  const { getLoggedIn, token } = useAuth();

  const navItems = [
    { id: "dashboard", label: "Overview", icon: BarChart2, href: "/dashboard" },
    { id: "rentals", label: "Rentals", icon: FileText, href: "/rentals" },
    { id: "payout", label: "Payouts", icon: DollarSign, href: "/payouts" },
    { id: "vehicles", label: "Vehicles", icon: Truck, href: "/vehicles" },
    { id: "settings", label: "Settings", icon: Settings, href: "/settings" },
  ];

  // Handler to toggle drawer, pass to Header
  const handleToggleDrawer = () => setDrawerOpen((open) => !open);
  const handleCloseDrawer = () => setDrawerOpen(false);

  useEffect(() => {
    if (token){
      getLoggedIn(() => {}, token!)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  

  // Sidebar content as a function for reuse
  const SidebarContent = (
    <div className="h-full flex flex-col bg-[#0A0A14] text-white">
      <div className="p-6">
        <Link to="/" className="flex items-center">
          <img src={logo} alt="" width={180} />
        </Link>
      </div>
      <nav className="flex-1">
        <ul className="space-y-2">
          {navItems.map((item) => (
            <li key={item.id}>
              <Link
                to={item.href}
                className={cn(
                  "flex items-center px-6 py-3 text-sm font-medium",
                  activeNav === item.id ? "bg-blue-600 text-white" : "text-gray-400 hover:bg-gray-800"
                )}
                onClick={() => {
                  setActiveNav(item.id);
                  if (!screens.md) handleCloseDrawer();
                }}
              >
                <item.icon className="w-5 h-5 mr-3" />
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
      <div className="p-4 border-t border-gray-800 h-[180px]">
        <div className="flex items-center">
          <div className="w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center text-white text-xs">            
            {`${user?.name?.charAt(0).toUpperCase() ?? ''} ${user?.name?.split(" ")[1]?.charAt(0).toUpperCase() ?? ""}`}
          </div>
          <div className="ml-2 flex-1">
            <div className="text-xs font-medium">
              {`${user?.name?.charAt(0).toUpperCase() ?? ''}${user?.name.slice(1, -1) ?? ""}`}
            </div>
            <div className="text-xs text-gray-400">{user?.email}</div>
          </div>
          <div className="text-gray-400">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </div>
        </div>
        <div>
          <Button
            variant="ghost"
            className={cn(
              "w-full flex items-center text-sm font-medium text-red-500 hover:bg-gray-800 justify-start p-0 mt-4 hover:bg-transparent hover:text-red-500 hover:underline"
            )}
            onClick={() => logOut()}
          >
            <Logout />
            Logout
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <Layout style={{ minHeight: "100vh", overflow: "auto" }}>
      {/* Sider for desktop, Drawer for mobile */}
      {screens.md ? (
        <Sider
          width={256}
          style={{ background: "#0A0A14", color: "white" }}
          breakpoint="md"
          collapsedWidth={0}
        >
          {SidebarContent}
        </Sider>
      ) : (
        <Drawer
          placement="left"
          open={drawerOpen}
          onClose={handleCloseDrawer}
          styles={{
            body: { padding: 0, background: "#0A0A14" },
            header: { background: "#0A0A14", color: "#ffffff" },
          }}
          headerStyle={{ background: "#0A0A14", color: "#fff" }}
          title={<span style={{ color: "#fff" }}>Menu</span>}
          closeIcon={<X color="#fff" className="w-5 h-5" />}
          width={256}
        >
          {SidebarContent}
        </Drawer>
      )}

      <Layout>
        {/* Header: pass toggle handler as prop */}
        <Header onToggleDrawer={handleToggleDrawer} />
        <Content
          style={{
            padding: 0,
            height: "100vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Content area: horizontally scrollable on mobile */}
          <div
            className="flex-1 mb-16 dashboard-content-scroll"
            style={{
              padding: screens.xs ? 16 : 24,
              overflowX: screens.md ? "auto" : "auto",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
}

